import React, { useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateClient, useUpdateClient } from '../../hooks/useClients';
import { validateRut, formatRut, cleanRut } from '../../utils/rutValidator';
import { useToast } from '../../hooks/useToast';
import { ApiError } from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import type { Client, ClientCreate, ClientUpdate } from '../../types/client';

interface ClientFormData {
  rut: string;
  name: string;
  fantasyName: string;
  address: string;
  email: string;
  phone: string;
  notes: string;
  active: boolean;
}

export interface ClientFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: Client | null;
  readOnly?: boolean;
  onSuccess?: () => void;
}

const ClientForm: React.FC<ClientFormProps> = ({
  open,
  onOpenChange,
  client,
  readOnly = false,
  onSuccess,
}) => {
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const { showError: showToastError } = useToast();

  const isEditMode = !!client;

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ClientFormData>({
    defaultValues: {
      rut: '',
      name: '',
      fantasyName: '',
      address: '',
      email: '',
      phone: '',
      notes: '',
      active: true,
    },
  });

  // Reset form when dialog opens or client changes
  useEffect(() => {
    if (open) {
      if (client) {
        reset({
          rut: formatRut(client.rut),
          name: client.name,
          fantasyName: client.fantasyName || '',
          address: client.address || '',
          email: client.email || '',
          phone: client.phone || '',
          notes: client.notes || '',
          active: client.active,
        });
      } else {
        reset({
          rut: '',
          name: '',
          fantasyName: '',
          address: '',
          email: '',
          phone: '',
          notes: '',
          active: true,
        });
      }
    }
  }, [open, client, reset]);

  // Format RUT on blur for display
  const handleRutBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      if (!value) return;

      const cleaned = cleanRut(value);
      if (cleaned.length >= 2) {
        const result = validateRut(value);
        if (result.valid && result.cleanRut) {
          setValue('rut', formatRut(result.cleanRut), { shouldValidate: false });
        }
      }
    },
    [setValue]
  );

  const handleApiError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError) {
        const statusCode = error.statusCode;
        const details = error.details;

        if (statusCode === 409) {
          // Duplicate RUT - show inline on rut field
          const message =
            details?.message || details?.detail || error.message || 'Ya existe un cliente con este RUT';
          setError('rut', { type: 'server', message });
          return;
        }

        if (statusCode === 400) {
          // Field validation error from API - try to show inline
          const errorMessage = details?.message || details?.detail || error.message || '';
          const lowerMessage = errorMessage.toLowerCase();

          if (lowerMessage.includes('rut')) {
            setError('rut', { type: 'server', message: errorMessage });
            return;
          }
          if (lowerMessage.includes('name') || lowerMessage.includes('nombre')) {
            setError('name', { type: 'server', message: errorMessage });
            return;
          }
          if (lowerMessage.includes('email')) {
            setError('email', { type: 'server', message: errorMessage });
            return;
          }
          if (lowerMessage.includes('phone') || lowerMessage.includes('teléfono')) {
            setError('phone', { type: 'server', message: errorMessage });
            return;
          }
          if (lowerMessage.includes('address') || lowerMessage.includes('dirección')) {
            setError('address', { type: 'server', message: errorMessage });
            return;
          }
          if (lowerMessage.includes('fantasía') || lowerMessage.includes('fantasy')) {
            setError('fantasyName', { type: 'server', message: errorMessage });
            return;
          }
          if (lowerMessage.includes('notes') || lowerMessage.includes('notas')) {
            setError('notes', { type: 'server', message: errorMessage });
            return;
          }

          // Generic 400 - show on first field as general message
          setError('root', { type: 'server', message: errorMessage });
          return;
        }

        // Network or server errors (5xx, 0) - show as toast, retain input
        showToastError(error, 'Error al guardar el cliente');
      } else {
        // Unknown error - show as toast
        showToastError(error, 'Error de conexión al guardar el cliente');
      }
    },
    [setError, showToastError]
  );

  const onSubmit = async (data: ClientFormData) => {
    // Validate RUT before submitting
    const rutValidation = validateRut(data.rut);
    if (!rutValidation.valid) {
      setError('rut', {
        type: 'validate',
        message: rutValidation.error || 'RUT inválido',
      });
      return;
    }

    const cleanedRut = rutValidation.cleanRut!;

    try {
      if (isEditMode && client) {
        const updateData: ClientUpdate = {
          rut: cleanedRut,
          name: data.name.trim(),
          fantasyName: data.fantasyName.trim() || undefined,
          address: data.address.trim() || undefined,
          email: data.email.trim() || undefined,
          phone: data.phone.trim() || undefined,
          notes: data.notes.trim() || undefined,
          active: data.active,
        };
        await updateClient.mutateAsync({
          clientId: client.clientId,
          data: updateData,
        });
      } else {
        const createData: ClientCreate = {
          rut: cleanedRut,
          name: data.name.trim(),
          fantasyName: data.fantasyName.trim() || undefined,
          address: data.address.trim() || undefined,
          email: data.email.trim() || undefined,
          phone: data.phone.trim() || undefined,
          notes: data.notes.trim() || undefined,
        };
        await createClient.mutateAsync(createData);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      handleApiError(error);
    }
  };

  const isLoading = createClient.isLoading || updateClient.isLoading || isSubmitting;

  const title = readOnly
    ? 'Detalle del Cliente'
    : isEditMode
    ? 'Editar Cliente'
    : 'Crear Cliente';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {readOnly
              ? 'Información del cliente (solo lectura)'
              : isEditMode
              ? 'Modifica los datos del cliente'
              : 'Ingresa los datos del nuevo cliente'}
          </DialogDescription>
        </DialogHeader>

        {errors.root && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
            {errors.root.message}
          </div>
        )}

        <form
          onSubmit={readOnly ? undefined : handleSubmit(onSubmit)}
          className="space-y-4"
        >
          {/* RUT */}
          <div>
            <Label htmlFor="rut">
              RUT <span className="text-red-500">*</span>
            </Label>
            <Input
              id="rut"
              placeholder="Ej: 76.543.210-K"
              disabled={readOnly}
              {...register('rut', {
                required: 'El RUT es requerido',
                validate: (value) => {
                  if (!value.trim()) return 'El RUT es requerido';
                  const result = validateRut(value);
                  return result.valid || result.error || 'RUT inválido';
                },
              })}
              onBlur={handleRutBlur}
              className={errors.rut ? 'border-red-500' : ''}
              aria-invalid={!!errors.rut}
              aria-describedby={errors.rut ? 'rut-error' : undefined}
            />
            {errors.rut && (
              <p id="rut-error" className="mt-1 text-sm text-red-600">
                {errors.rut.message}
              </p>
            )}
          </div>

          {/* Name (Razón Social) */}
          <div>
            <Label htmlFor="name">
              Razón Social <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Nombre legal del cliente"
              disabled={readOnly}
              {...register('name', {
                required: 'La razón social es requerida',
                validate: (value) =>
                  value.trim().length > 0 || 'La razón social es requerida',
                maxLength: {
                  value: 200,
                  message: 'La razón social no puede exceder 200 caracteres',
                },
              })}
              className={errors.name ? 'border-red-500' : ''}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'name-error' : undefined}
            />
            {errors.name && (
              <p id="name-error" className="mt-1 text-sm text-red-600">
                {errors.name.message}
              </p>
            )}
          </div>

          {/* Fantasy Name */}
          <div>
            <Label htmlFor="fantasyName">Nombre Fantasía</Label>
            <Input
              id="fantasyName"
              placeholder="Nombre comercial (opcional)"
              disabled={readOnly}
              {...register('fantasyName', {
                maxLength: {
                  value: 200,
                  message: 'El nombre fantasía no puede exceder 200 caracteres',
                },
              })}
              className={errors.fantasyName ? 'border-red-500' : ''}
              aria-invalid={!!errors.fantasyName}
              aria-describedby={errors.fantasyName ? 'fantasyName-error' : undefined}
            />
            {errors.fantasyName && (
              <p id="fantasyName-error" className="mt-1 text-sm text-red-600">
                {errors.fantasyName.message}
              </p>
            )}
          </div>

          {/* Address */}
          <div>
            <Label htmlFor="address">Dirección</Label>
            <Input
              id="address"
              placeholder="Dirección del cliente (opcional)"
              disabled={readOnly}
              {...register('address', {
                maxLength: {
                  value: 300,
                  message: 'La dirección no puede exceder 300 caracteres',
                },
              })}
              className={errors.address ? 'border-red-500' : ''}
              aria-invalid={!!errors.address}
              aria-describedby={errors.address ? 'address-error' : undefined}
            />
            {errors.address && (
              <p id="address-error" className="mt-1 text-sm text-red-600">
                {errors.address.message}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="contacto@empresa.cl (opcional)"
              disabled={readOnly}
              {...register('email', {
                validate: (value) => {
                  if (!value || !value.trim()) return true;
                  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  return (
                    emailPattern.test(value.trim()) ||
                    'El email tiene un formato inválido'
                  );
                },
              })}
              className={errors.email ? 'border-red-500' : ''}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && (
              <p id="email-error" className="mt-1 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="phone">Teléfono</Label>
            <Input
              id="phone"
              placeholder="+56912345678 (opcional)"
              disabled={readOnly}
              {...register('phone', {
                maxLength: {
                  value: 30,
                  message: 'El teléfono no puede exceder 30 caracteres',
                },
              })}
              className={errors.phone ? 'border-red-500' : ''}
              aria-invalid={!!errors.phone}
              aria-describedby={errors.phone ? 'phone-error' : undefined}
            />
            {errors.phone && (
              <p id="phone-error" className="mt-1 text-sm text-red-600">
                {errors.phone.message}
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notas</Label>
            <textarea
              id="notes"
              placeholder="Notas internas sobre el cliente (opcional)"
              disabled={readOnly}
              {...register('notes', {
                maxLength: {
                  value: 1000,
                  message: 'Las notas no pueden exceder 1000 caracteres',
                },
              })}
              rows={3}
              className={`flex w-full rounded-md border bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                errors.notes ? 'border-red-500' : 'border-gray-300'
              }`}
              aria-invalid={!!errors.notes}
              aria-describedby={errors.notes ? 'notes-error' : undefined}
            />
            {errors.notes && (
              <p id="notes-error" className="mt-1 text-sm text-red-600">
                {errors.notes.message}
              </p>
            )}
          </div>

          {/* Active (only in edit mode) */}
          {isEditMode && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                disabled={readOnly}
                {...register('active')}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <Label htmlFor="active" className="cursor-pointer">
                Cliente activo
              </Label>
            </div>
          )}

          {/* Footer buttons */}
          {!readOnly && (
            <DialogFooter className="pt-4">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading
                  ? 'Guardando...'
                  : isEditMode
                  ? 'Actualizar'
                  : 'Crear Cliente'}
              </button>
            </DialogFooter>
          )}

          {readOnly && (
            <DialogFooter className="pt-4">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cerrar
              </button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ClientForm;
